import React, { Component } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import isEmpty from 'lodash/isEmpty'
import { pick } from 'lodash'

// Components
import GeneralLoader from '../../GeneralLoader'
import DeleteConfirmationDialog from '../../utils/dialogs/DeleteConfirmationDialog'
import ErrorWrap from '../../utils/error-success/ErrorWrap'
import SuccessWrap from '../../utils/error-success/SuccessWrap'
import Button from '../../utils/Button'
import PersonalLocationFieldset from './form-components/PersonalLocationFieldset'
import PersonalInfoFieldset from './form-components/PersonalInfoFieldset'
import WorkLocationFieldset from './form-components/WorkLocationFieldset'
import AccountDetailsFieldset from './form-components/AccountDetailsFieldset'
import AccountStatusFieldset from './form-components/AccountStatusFieldset'
import EmergencyContactFieldset from './form-components/EmergencyContactFieldset'
import BioExperienceFieldset from './form-components/BioExperienceFieldset'
import CCAFieldset from './form-components/CCAFieldset'

// State
import { userTokenUpdate, userLogout } from '../../../store/actions/user'
import { getLocations } from '../../../store/actions/locations'
import { getSalutations } from '../../../store/actions/salutations'
import { getReviewerByAdmin } from '../../../store/actions/reviewers'
import { getSectors } from '../../../store/actions/sectors'
import { getPhoneTypes } from '../../../store/actions/phoneTypes'
import { getProfileStatuses } from '../../../store/actions/profileStatuses'
import { getUserTypes } from '../../../store/actions/userTypes'
import PastReviewsFormSection from './form-components/PastReviewsFormSection'

// CSS
import '../../../css/forms/custom-checkbox.css'
import '../../../css/forms/react-select.css'

// Utils
import { deleteTempImages } from '../../../utils/uploadHelpers'
import { formatMomentObject, formatMomentString } from '../../../utils/dateHelpers'
import {
	updateSelectedValue,
	handleKeyPress,
	handleCheckboxClick,
	handleRadioClick,
	editorChange,
	addInputToCollection,
	deleteInputFromCollection,
	handleCheckboxClickArray,
} from '../../../utils/forms/inputHandling'
import InfoChanges from './info-changes/InfoChanges'
import ManagersAlert from '../../utils/ManagersAlert'

class AdminReviewersForm extends Component {
	constructor(props) {
		super(props)

		// We need to define all data because it needs to exist for controlled inputs when data is "new"
		this.state = {
			savedData: {},
			data: {
				id: !!props.match.params.id ? props.match.params.id : null,

				// Personal Info
				salutation: '',
				title: '',
				fname: '',
				lname: '',
				organization: '',
				email: '',
				alternate_emails: [],
				phone: '',
				phone_alternate: '',
				pic: '',

				// Location
				address: '',
				city: '',
				postal: '',
				province: '',
				language: '',

				// Work Location
				address_work: '',
				city_work: '',
				postal_work: '',
				province_work: '',

				// Emergency Contact
				emergency_contact_name: '',
				emergency_relationship: '',
				emergency_contact_phone_home: '',
				emergency_contact_phone_home_extension: '',
				emergency_contact_phone_work: '',
				emergency_contact_phone_work_extension: '',
				emergency_contact_phone_mobile: '',
				emergency_contact_phone_mobile_extension: '',

				// Bio / Experience
				sectors: [],
				experience: '',
				bio: '',

				// CCA
				color: '',
				comments: '',
				cca_notes: '',
				cca_training: null,
				has_been_team_lead: 0,
				dropped_out_of_review: 0,
				last_score_client: '',
				last_score_cca: '',
				last_next_review: null,
				pre_cca_review_notes_withdrawals: '',

				// Account Status
				account_accepted: 1,
				status: 1,
				date_added: null,
			},
			isLoading: !!props.match.params.id ? true : false,
			addSucccess: !!props.location.state ? props.location.state.successMessage : '',
			updateLoading: false,
			updateErrors: {},
			updateSuccess: '',
			showDeleteConfirmationDialog: false,
			deletionInProgress: false,
			deleteErrors: '',
			deleteSuccess: '',
			promiseCollection: [],
			pic: '',
			info_changes: {},
		}

		// Remove state from history object (success messages, etc)
		if (!!props.location.state) {
			props.history.replace({
				pathname: props.history.location.pathname,
			})
		}
	}

	// ===================================================================================
	//  LIFE CYCLE
	// ===================================================================================
	componentDidMount() {
		if (this.state.data.id) {
			this.loadData()
		}

		this.props.getLocations()
		this.props.getSalutations()
		this.props.getSectors()
		this.props.getPhoneTypes()
		this.props.getProfileStatuses()
		this.props.getUserTypes()
	}

	loadData() {
		this.props.getReviewerByAdmin(this.state.data.id, this.props.user.token).then(resp => {
			if (resp.data.status === 403) {
				this.props.userLogout('admin')
				return
			}

			// If no response, bail
			if (!resp || !resp.data.reviewer.id) {
				this.setState({ isLoading: false })
				return
			}

			const data = this.formatRecordData(resp.data.reviewer)

			// Set the component's data
			this.setState({
				info_changes: resp.data.info_changes,
				data,
				savedData: data,
				isLoading: false,
			})
		})
	}

	formatRecordData = data => {
		// Ensure dates are valid
		const date_added = formatMomentString(data.date_added)
		const date_edited = formatMomentString(data.date_edited)
		const cca_training = formatMomentObject(data.cca_training)
		const last_next_review = formatMomentObject(data.last_next_review)

		// React doesn't like it when input elements have values of null, so set them to empty
		Object.keys(data).map(function(key) {
			if (data[key] === null && key !== 'last_next_review' && key !== 'cca_training') {
				data[key] = ''
			}

			if (key === 'alternate_emails' && !data[key].length) {
				data[key] = ['']
			}

			// Return something just so browser doesn't throw a warning about map not returning anything
			return data[key]
		})

		return { ...data, date_added, date_edited, last_next_review, cca_training }
	}

	handleInputChange = event => {
		const { id, value } = event.target

		this.setState({
			data: {
				...this.state.data,
				[id]: value,
			},
		})
	}

	handleInputChangeArray = async (event, index) => {
		const { name, value } = event.target

		await this.setState({
			data: {
				...this.state.data,
				[name]: this.state.data[name].map((item, i) => {
					if (i !== index) {
						return item
					}

					return value
				}),
			},
		})
	}

	// ===================================================================================
	//  SECTOR-SPECIFIC INPUT HANDLING
	// ===================================================================================
	handleInputChangeSector = (event, id) => {
		let { value } = event.target

		// Value should be treated as an integer
		value = parseInt(value, 10)

		// Clone array
		let newState = [...this.state.data.sectors]

		// Find index of the id within the cloned array
		const indexOf = newState.findIndex(item => item.id === id)

		// If not found, bail
		if (indexOf < 0) return

		newState[indexOf].years = value

		this.setState({
			data: {
				...this.state.data,
				sectors: [...newState],
			},
		})
	}

	handleCheckboxClickSector = (event, name) => {
		let { value } = event.target

		// Value should be treated as an integer
		value = parseInt(value, 10)

		// Clone array
		let newState = [...this.state.data[name]]

		// Find index of the id within the cloned array
		const indexOf = newState.findIndex(item => item.id === value)

		// If index was not found (-1) then we should immutably concat() the a new object onto the array
		if (indexOf < 0) {
			newState = newState.concat({
				id: value,
				years: null,
			})
		}
		// The index was found, so let's remove it immutably
		else {
			newState = newState.slice(0, indexOf).concat(newState.slice(indexOf + 1))
		}

		this.setState({
			data: {
				...this.state.data,
				[name]: [...newState],
			},
		})
	}

	handleCheckboxClick = (event, name) => {
		let { value } = event.target

		this.setState({
			data: {
				...this.state.data,
				[name]: parseInt(value),
			},
		})
	}

	clearPic = () => {
		this.setState({
			data: {
				...this.state.data,
				pic: '',
			},
		})
	}

	// ===================================================================================
	//  SUBMIT
	// ===================================================================================
	handleSubmit = () => {
		this.setState(
			{
				addSucccess: '',
				updateErrors: {},
				updateLoading: true,
				updateSuccess: '',
			},
			() => {
				Promise.all(this.state.promiseCollection)
					.then(resolves => {
						this.setState({
							promiseCollection: [],
						})

						this.completeSubmit(resolves)
					})
					.catch(err => {
						this.setState({
							updateLoading: false,
							promiseCollection: [],
						})
					})
			}
		)
	}

	completeSubmit = resolves => {
		const { user, role } = this.props

		// Get chunked image results for file.
		// Either array of objects, or null
		const pic = resolves[0]
			? resolves.filter(obj => {
					if (!obj) return false
					return obj[0].type === 'pic'
			  })[0]
			: null

		// Put request data together
		// If role is set to manager then we will only save from the CCA Information section
		let data = {}

		if (role === 'manager') {
			data = {
				...this.state.savedData,
				cca_notes: this.state.data.cca_notes,
				cca_training: this.state.data.cca_training,
				has_been_team_lead: this.state.data.has_been_team_lead,
				dropped_out_of_review: this.state.data.dropped_out_of_review,
				last_score_client: this.state.data.last_score_client,
				last_score_cca: this.state.data.last_score_cca,
				last_next_review: this.state.data.last_next_review,
				pre_cca_review_notes_withdrawals: this.state.data.pre_cca_review_notes_withdrawals,
			}
			return
		} else {
			data = {
				...this.state.data,
				new_pic: !!pic ? pick(pic[0].results, ['dirName', 'filename']) : '',
			}
		}

		// We might need to delete these later
		const uploadedUrls = []
		if (!!pic) uploadedUrls.push(data.new_pic)

		const token = user.token
		const AUTH_HEADER = { Authorization: process.env.REACT_APP_SITE_KEY + ':Bearer ' + token }
		return axios
			.post(process.env.REACT_APP_API_URL + 'reviewer/update', data, { headers: AUTH_HEADER })
			.then(response => {
				if (!!response.data.token) {
					this.props.userTokenUpdate('admin', response.data.token)
				}

				if (response.data.status === 403) {
					this.props.userLogout('admin')
				} else if (
					response.status !== 200 ||
					(response.data.status && response.data.status !== 200)
				) {
					this.setState({
						updateLoading: false,
						updateErrors: !isEmpty(response.data.errors)
							? response.data.errors
							: { general: response.data.message },
					})

					deleteTempImages(uploadedUrls, this.props.user.token).catch(err => {
						console.log(err)
					})
				} else {
					// Redirect to store edit route

					// DOES NOT WORK
					// if (!data.id) {
					// 	console.log("here is the value for the response data ", response.data)
					// 	this.props.history.push({
					// 		pathname: "/admin/reviewers/edit/" + response.data.reviewer.id,
					// 		state: { successMessage: response.data.message }
					// 	});
					// 	return;
					// }

					// DOES NOT WORK
					// Format record data returned from API
					// const reviewerRecord = this.formatRecordData(response.data.reviewer);

					//Get pic only if the account is being updated.
					if (typeof response.data.reviewer !== 'undefined' || response.data.reviewer != null) {
						this.setState(prevState => ({
							data: {
								...prevState.data,
								pic: !!response.data.reviewer.pic ? response.data.reviewer.pic : '',
							},
						}))
					}
					this.setState({
						// data: reviewerRecord, DOES NOT WORK
						updateLoading: false,
						updateSuccess: 'You have successfully updated a reviewer!',
					})
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						updateLoading: false,
						updateErrors: {
							general: err.response.data.error
								? err.response.data.error
								: err.response.data.message,
						},
					})
				} else {
					this.setState({ updateLoading: false, updateErrors: { general: err.message } })
				}

				deleteTempImages(uploadedUrls, this.props.user.token).catch(err => {
					console.log(err)
				})
			})
	}

	// ===================================================================================
	//  DELETE
	// ===================================================================================
	handleDelete = () => {
		const { user } = this.props

		this.setState({
			deletionInProgress: true,
			deleteErrors: {},
			deleteSuccess: '',
		})

		const token = user.token
		const AUTH_HEADER = { Authorization: process.env.REACT_APP_SITE_KEY + ':Bearer ' + token }
		return axios
			.delete(process.env.REACT_APP_API_URL + 'reviewer/' + this.state.data.id, {
				headers: AUTH_HEADER,
			})
			.then(response => {
				if (!!response.data.token) {
					this.props.userTokenUpdate('admin', response.data.token)
				}

				if (response.data.status === 403) {
					this.props.userLogout('admin')
				} else if (
					response.status !== 200 ||
					(response.data.status && response.data.status !== 200)
				) {
					this.setState({
						deletionInProgress: false,
						deleteErrors: !isEmpty(response.data.errors)
							? response.data.errors
							: response.data.message,
					})
				} else {
					this.setState({
						deletionInProgress: false,
						deleteSuccess: response.data.message,
					})
				}
			})
			.catch(err => {
				if (err.response) {
					this.setState({
						deletionInProgress: false,
						deleteErrors: err.response.data.error
							? err.response.data.error
							: err.response.data.message,
					})
				} else {
					this.setState({
						deletionInProgress: false,
						deleteErrors: err.message,
					})
				}
			})
	}

	handleDeleteRedirect() {
		this.props.history.push('/admin/reviewers')
	}

	render() {
		const {
			locationsLoading,
			locationsError,
			provinces,
			salutationsLoading,
			salutationsError,
			salutations,
			sectorsLoading,
			sectorsError,
			phoneTypes,
			phoneTypesLoading,
			phoneTypesError,
			profileStatuses,
			profileStatusesLoading,
			profileStatusesError,
			userTypes,
			userTypesLoading,
			userTypesError,
			sectors,
		} = this.props
		const {
			data,
			info_changes,
			addSucccess,
			updateErrors,
			updateSuccess,
			updateLoading,
		} = this.state
		return (
			<React.Fragment>
				{!this.props.match.params.id && <h1>Add Reviewer</h1>}

				{/* Manager Alert - Won't appear for admins */}
				<ManagersAlert text='As a manager you may only make edits to the "CCA Information" field. Changes to other fields will not be saved.' />

				{/* Loading */}
				{this.state.isLoading ? (
					<GeneralLoader />
				) : (
					<React.Fragment>
						{/* Success */}
						{!!addSucccess && <SuccessWrap animate={1}>{addSucccess}</SuccessWrap>}

						{/* General error (record load error) */}
						{!!this.props.recordError && (
							<ErrorWrap animate={1}>{this.props.recordError}</ErrorWrap>
						)}

						{/* Record not found */}
						{!!data.id && !data.fname && (
							<ErrorWrap animate={1}>{'Unable to find record'}</ErrorWrap>
						)}

						{/* Show form if adding new item, or item was selected and found */}
						{(!data.id || (data.id && data.fname)) && (
							<React.Fragment>
								{info_changes && !!this.props.match.params.id && (
									<InfoChanges
										date_modified={info_changes.date_modified}
										oldData={info_changes.data}
										newData={data}
									/>
								)}
								<PersonalInfoFieldset
									data={data}
									errors={updateErrors}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									handleInputChangeArray={this.handleInputChangeArray}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res => this.setState(res))
									}}
									handleRadioClick={(e, name) =>
										handleRadioClick(e, name, this.state).then(res => this.setState(res))
									}
									handleCheckboxClickArray={(obj, name) =>
										handleCheckboxClickArray(obj, name, this.state).then(res => this.setState(res))
									}
									addInputToCollection={id =>
										addInputToCollection(id, this.state).then(res => this.setState(res))
									}
									deleteInputFromCollection={(id, name) =>
										deleteInputFromCollection(id, name, this.state).then(res => this.setState(res))
									}
									addImagePromise={promise => this.state.promiseCollection.push(promise)}
									clearPic={this.clearPic}
									clearImageUploader={this.state.updateSuccess}
									salutations={salutations}
									salutationsError={salutationsError}
									salutationsLoading={salutationsLoading}
									phoneTypes={phoneTypes}
									phoneTypesError={phoneTypesError}
									phoneTypesLoading={phoneTypesLoading}
									inProcess={updateLoading}
								/>
								<PersonalLocationFieldset
									data={data}
									errors={updateErrors}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res => this.setState(res))
									}}
									provinces={provinces}
									locationsError={locationsError}
									locationsLoading={locationsLoading}
									inProcess={updateLoading}
								/>
								<WorkLocationFieldset
									data={data}
									errors={updateErrors}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res => this.setState(res))
									}}
									provinces={provinces}
									locationsError={locationsError}
									locationsLoading={locationsLoading}
									inProcess={updateLoading}
								/>
								<EmergencyContactFieldset
									data={data}
									errors={updateErrors}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									inProcess={updateLoading}
								/>
								<BioExperienceFieldset
									data={data}
									errors={updateErrors}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									editorChange={(id, e) =>
										editorChange(id, e, this.state).then(res => this.setState(res))
									}
									handleCheckboxClickSector={this.handleCheckboxClickSector}
									handleInputChangeSector={this.handleInputChangeSector}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res => this.setState(res))
									}}
									sectors={sectors}
									sectorsError={sectorsError}
									sectorsLoading={sectorsLoading}
									inProcess={updateLoading}
								/>
								<CCAFieldset
									data={data}
									errors={updateErrors}
									handleKeyPress={e => handleKeyPress(e, this.handleSubmit)}
									handleInputChange={this.handleInputChange}
									handleCheckboxClick={this.handleCheckboxClick}
									editorChange={(id, e) =>
										editorChange(id, e, this.state).then(res => this.setState(res))
									}
									handleInputChangeSector={this.handleInputChangeSector}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res => this.setState(res))
									}}
									inProcess={updateLoading}
								/>

								{/* ============ ACCOUNT DETAILS ============ */}
								{!!data.id && <AccountDetailsFieldset data={data} inProcess={updateLoading} />}

								{/* ============ ACCOUNT STATUS ============ */}
								<AccountStatusFieldset
									data={data}
									errors={updateErrors}
									handleCheckboxClick={e =>
										handleCheckboxClick(e, this.state).then(res => this.setState(res))
									}
									updateSelectedValue={(value, id) => {
										updateSelectedValue(value, id, this.state).then(res => this.setState(res))
									}}
									handleRadioClick={(e, name) =>
										handleRadioClick(e, name, this.state).then(res => this.setState(res))
									}
									profileStatuses={profileStatuses}
									profileStatusesError={profileStatusesError}
									profileStatusesLoading={profileStatusesLoading}
									userTypes={userTypes}
									userTypesLoading={userTypesLoading}
									userTypesError={userTypesError}
									inProcess={updateLoading}
								/>

								{/* This is where I plan to put the past reviews for a reviewer */}
								<PastReviewsFormSection id={this.state.data.id} />

								<div className='btn_container'>
									{!!updateErrors.general && (
										<ErrorWrap animate={1}>{updateErrors.general}</ErrorWrap>
									)}
									{!isEmpty(updateErrors) && (
										<ErrorWrap animate={1}>
											There was an error. Please check the form for any error messages.
										</ErrorWrap>
									)}

									{updateSuccess && (
										<SuccessWrap>
											{!!data.id ? updateSuccess : 'Successfully created a new reviewer.'}
										</SuccessWrap>
									)}

									<Button
										text={!!data.id ? 'Update' : 'Create'}
										clickHandler={this.handleSubmit}
										isLoading={updateLoading}
									/>
									{!!data.id &&
										(this.props.role === 'manager' ? (
											<React.Fragment></React.Fragment>
										) : (
											<Button
												text={'Delete'}
												classes={'delete-btn'}
												clickHandler={() =>
													this.setState({
														showDeleteConfirmationDialog: true,
													})
												}
												isLoading={updateLoading}
											/>
										))}
								</div>

								<DeleteConfirmationDialog
									show={this.state.showDeleteConfirmationDialog}
									deletionInProgress={this.state.deletionInProgress}
									id={data.id}
									itemTitle={data.fname}
									handleDelete={this.handleDelete.bind(this)}
									handleDeleteRedirect={this.handleDeleteRedirect.bind(this)}
									handleClose={() => this.setState({ showDeleteConfirmationDialog: false })}
									deleteErrors={this.state.deleteErrors}
									deleteSuccess={this.state.deleteSuccess}
								/>
							</React.Fragment>
						)}
					</React.Fragment>
				)}
			</React.Fragment>
		)
	}
}

const mapStateToProps = state => {
	return {
		user: state.user.admin,
		role: state.user.admin.user.role,
		recordError: state.reviewers.errors,
		isLoading: state.user.isLoading,
		provinces: state.locations.provinces,
		locationsLoading: state.locations.isLoading,
		locationsError: state.locations.error,
		salutations: state.salutations.salutations,
		salutationsLoading:
			state.salutations.isLoading !== undefined ? state.salutations.isLoading : true,
		salutationsError: state.salutations.error,
		sectors: state.sectors.sectors,
		sectorsLoading: state.sectors.isLoading !== undefined ? state.sectors.isLoading : true,
		sectorsError: state.sectors.error,
		phoneTypes: state.phoneTypes.types,
		phoneTypesLoading: state.phoneTypes.isLoading !== undefined ? state.phoneTypes.isLoading : true,
		phoneTypesError: state.phoneTypes.error,
		profileStatuses: state.profileStatuses.statuses,
		profileStatusesLoading:
			state.profileStatuses.isLoading !== undefined ? state.profileStatuses.isLoading : true,
		profileStatusesError: state.profileStatuses.error,
		userTypes: state.userTypes.types,
		userTypesLoading: state.userTypes.isLoading !== undefined ? state.userTypes.isLoading : true,
		userTypesError: state.profileStatuses.error,
	}
}

export default connect(mapStateToProps, {
	userTokenUpdate,
	userLogout,
	getReviewerByAdmin,
	getLocations,
	getSalutations,
	getSectors,
	getPhoneTypes,
	getProfileStatuses,
	getUserTypes,
})(AdminReviewersForm)



// WEBPACK FOOTER //
// ./src/components/admin/reviewers/AdminReviewersForm.js